import { orderBy } from 'lodash';
import { useQuery } from 'react-query';

import { useConfig } from '../components/ConfigContext';
import { getQueryStatus, load } from '../lib';

const getQuery = filters => {
  const { scheduleId, groupId, teamId } = filters;

  const query = [
    { scheduleId },
    { groupId },
  ]
  if (teamId) query.push({ teamId })

  return { and: query };
}

const handleResults = results => results.map(result => {
  const { participant = {}, team, group, ...stats } = result;
  participant.team = team;
  participant.group = group;
  participant.stats = stats;

  return participant;
})

export const loadScheduleTotal = (filters, participantType, apiUrl) => {
  const { scheduleId } = filters;

  if (!scheduleId) return Promise.resolve(0);

  const request = load(`${apiUrl}/schedules/${scheduleId}/${participantType}Stats/count`, {
    where: getQuery(filters),
  })
  const result = request
    .then(result => result.count)

  result.cancel = request.cancel;
  return result;
}

export const loadScheduleStats = (filters, participantType, currentPage, perPage, querySort, queryOrder, apiUrl) => {
  const { scheduleId } = filters;
  if (!scheduleId) return Promise.resolve([]);

  let limit = perPage;
  let skip = (currentPage - 1) * perPage;
  let order = ['ranking ASC', 'group.name ASC'];
  if (limit < 0) {
    limit = 0;
  }
  if (skip < 0) {
    skip = 0;
  }
  if (querySort && queryOrder) {
    const rankingOrder = (queryOrder == 'ASC') ? 'DESC' : 'ASC';
    order = [`${querySort} ${queryOrder}`, `ranking ${rankingOrder}`, 'group.name ASC'];
  }

  const request = load(`${apiUrl}/schedules/${scheduleId}/${participantType}Stats`, {
    filter: {
      where: getQuery(filters),
      include: [{ participant: 'profile' }, 'team', 'group'],
      order,
      limit,
      skip,
    }
  })
  const result = request.then(handleResults)

  result.cancel = request.cancel;
  return result;
}

export const loadOfficeTotal = (filters, participantType, defaultOfficeId, apiUrl) => {
  const { officeId, ...where } = filters;
  const request = load(`${apiUrl}/offices/${officeId || defaultOfficeId}/${participantType}Stats/count`, {
    where,
  })
  const result = request.then(result => result.count)

  result.cancel = request.cancel;
  return result;
}

export const loadOfficeStats = (filters, participantType, defaultOfficeId, currentPage, perPage, querySort, queryOrder, apiUrl) => {
  let limit = perPage;
  let skip = (currentPage - 1) * perPage;
  let order = null;
  if (limit < 0) {
    limit = 0;
  }
  if (skip < 0) {
    skip = 0;
  }
  if (querySort && queryOrder) {
    const rankingOrder = (queryOrder == 'ASC') ? 'DESC' : 'ASC';
    order = [`${querySort} ${queryOrder}`, `ranking ${rankingOrder}`];
  }

  const { officeId, ...where } = filters;
  const request = load(`${apiUrl}/offices/${officeId || defaultOfficeId}/${participantType}Stats`, {
    filter: {
      where,
      include: [{ participant: 'profile' }, 'team'],
      order,
      limit,
      skip,
    }
  })
  const result = request.then(handleResults)

  result.cancel = request.cancel;
  return result;
}

export const useStats = (filters, { participantType, type, currentPage, perPage, totalOptions, sorts, orders, querySort, queryOrder, ...options }) => {
  const { apiUrl, officeId: defaultOfficeId } = useConfig();

  const { data: total, status: totalStatus } = useQuery(
    ['stats-total', filters, participantType, type],
    () => {
      if (type === 'League') return loadOfficeTotal(filters, participantType, defaultOfficeId, apiUrl)
      return loadScheduleTotal(filters, participantType, apiUrl)
    },
    totalOptions
  )

  const { status: dataStatus, ...meta } = useQuery(
    ['stats-data', filters, participantType, type, currentPage, perPage, querySort, queryOrder],
    () => {
      if (type === 'League') return loadOfficeStats(filters, participantType, defaultOfficeId, currentPage, perPage, querySort, queryOrder, apiUrl);
      return loadScheduleStats(filters, participantType, currentPage, perPage, querySort, queryOrder, apiUrl);
    },
    { enabled: currentPage != null, ...options }
  )

  let { data } = meta;
  if (sorts && orders && !querySort) {
    data = orderBy(data, sorts, orders.map(order => order.toLowerCase()));
  }

  const status = getQueryStatus(totalStatus, dataStatus);
  return { total, status, ...meta, data }
}
